import { Logger } from "../../clients/logger";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import { System } from "../../common/system";
import AssimilationMovingWindow from "./assimilationMovingWindow";
import SystemManagerClient from "../../common/systemManagerClient";
import { BaseWindow } from "../window/WindowAbstractions/BaseWindow";
import AssimilationHelper from "./helper";
import { SubscriberResponse, RouterClient } from "../../clients/routerClient";
import { StandardError } from "../../types";
import { Bounds } from "../../services/window/types";
import { setWindowState } from "../../clients/workspaceClient";
import { FinsembleNativeWindowObject } from "./types";
import uuidv4 from "uuid-random";
import get from "lodash/get";

type FinsembleWindowObject = Record<string, any>;
type Message = Record<string, any>;
type MoveWindowData = Record<string, any>;

/**
 *The assimilation window manager handles all interaction with native windows. It also keeps track of them.
 *
 * @export
 * @class AssimilationWindowManager
 */
export default class AssimilationWindowManager {
	assimilationHelper: AssimilationHelper;

	// This is a list of all hooked windows
	activeWindows: Record<string, any>;

	// This is a list of windows
	awaitingAssimilationSpawn: Record<string, any>;

	// This is a list of windows
	awaitingSpawn: Record<string, any>;

	// This is a list of all windows on the system (OS level). These may not be hooked.
	allWindows: Record<string, any>;

	movingWindow: Record<string, any> | null;

	floatingTitlebar: string | null;

	ws?: WebSocket;

	dockedToolbarOnStart: boolean;

	constructor(assimilationHelper: AssimilationHelper) {
		this.assimilationHelper = assimilationHelper;
		this.activeWindows = {};
		this.awaitingAssimilationSpawn = {};
		this.awaitingSpawn = {};
		this.allWindows = {};
		this.movingWindow = null;
		this.floatingTitlebar = null;
		this.updateActiveWindowHeartbeats();
		this.dockedToolbarOnStart = false;
	}

	/**
	 * Set the websocket so we can send socket messages directly from here
	 *
	 * @param {*} ws
	 * @memberof AssimilationWindowManager
	 */
	setWebsocket(ws: WebSocket) {
		this.ws = ws;
	}

	sendSocketMessage(message: string | ArrayBufferLike | Blob | ArrayBufferView) {
		if (!this.ws) {
			return setTimeout(() => {
				this.sendSocketMessage(message);
			}, 100);
		}
		this.ws.send(message);
	}

	/**
	 * setMovingWindow sets the moving window and wraps if into an object that houses calculations that are needed for movement
	 *
	 * @param {*} windowObject
	 * @memberof AssimilationWindowManager
	 */
	setMovingWindow(windowObject: FinsembleWindowObject) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.setMovingWindow", windowObject);
		return (this.movingWindow = new AssimilationMovingWindow(windowObject));
	}

	/**
	 *returns the current moving window
	 *
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	getMovingWindow() {
		Logger.system.debug("Assimilation.AssimilationWindowManager.getMovingWindow");
		return this.movingWindow;
	}

	/**
	 *get a window by it's handle uuid
	 *
	 * @param {*} windowHandle
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	getWindowByUUID(windowHandle: string) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.getWindowByUUID:", windowHandle);
		return this.activeWindows[windowHandle];
	}

	/**
	 *returns a window by using it's window name
	 *
	 * @param {*} windowName
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	getWindowByName(windowName: string) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.getWindowByName:", windowName);
		const activeWindowKeys = Object.keys(this.activeWindows);
		for (let i = 0; i < activeWindowKeys.length; i++) {
			const activeWindow = this.activeWindows[activeWindowKeys[i]];
			if (activeWindow.windowName === windowName) return activeWindow;
		}
	}

	/**
	 *This moves windows from awaiting to active in our list
	 *
	 * @param {string} ref
	 * @param {string} uuid The window handle of the window
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	updateSpawningWindow(ref: string, uuid: string) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.updateSpawningWindow:", ref, uuid);
		const windowObject = this.awaitingAssimilationSpawn[ref];
		if (!windowObject) return;
		if (!uuid) {
			// This application doesn't have a window;
			windowObject.windowLess = true;
			uuid = ref;
		}
		windowObject.uuid = uuid;
		this.activeWindows[uuid] = windowObject;
		delete this.awaitingAssimilationSpawn[ref];

		return windowObject;
	}

	/**
	 * We use this if the window is already spawned and we have the uuid
	 *
	 * @param {*} windowObject
	 * @param {*} uuid
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	updateSpawningWindowFromWindowObject(message: Message): Promise<{
		wrap: FinsembleWindow;
	}> {
		const windowResponse = message.data;
		const identifier = {
			name: message.data.name,
			windowType: "FinsembleNativeWindow",
		};
		return FinsembleWindow.getInstance(identifier, async (err: StandardError, windowWrap: FinsembleWindow) => {
			if (windowWrap && windowWrap.descriptor) {
				windowWrap.descriptor.hideTitleBar = true;
			}
			windowWrap.uuid = parseInt(windowResponse.windowHandle, 10).toString(16);
			this.activeWindows[windowWrap.uuid] = windowWrap;
			const bounds = await windowWrap.getBounds();
			bounds.data ? this.handleSpawnComplete(windowWrap, bounds.data) : null;
		});
	}

	/**
	 *This is called once we've received the window back from assimilation spawn
	 *
	 * @param {*} windowObject - our window wrap
	 * @param {*} bounds - the bounds to set the window to
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	handleSpawnComplete(windowObject: FinsembleWindowObject, bounds: Bounds): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.handleSpawnComplete:", windowObject, bounds);

		if (!windowObject) return Logger.system.error("no window reference found");

		// Users can opt a window out of participating in Fsbl's window management by setting this field to false
		// (however, we to default to true).
		const manageWindowMovement = get(
			windowObject,
			"descriptor.customData.foreign.services.windowService.manageWindowMovement",
			true
		);

		if (bounds && manageWindowMovement) {
			// bounds are null for windowLess components
			const canHook = this.assimilationHelper.canHookWindow(windowObject);
			if (canHook) {
				// Set bounds call can occur before the window is ready
				windowObject.location = bounds;
				windowObject.setBounds({ bounds });

				// Any router calls regarding updated window should only happen once the window is ready
				windowObject.onReady(() => {
					/**
					 * TP - 30/09/2021 - Following the code this will get handled by the externalWindowWrapper
					 * which will set the window final location and trigger a bounds changed event.
					 */
					RouterClient.transmit(`${windowObject.name}.endMovement`, {
						location: windowObject.location,
					});
				});
			}
		}
		this.registerWindow(windowObject, windowObject);
	}

	/**
	 * This will add a window to our full list of windows. These windows may not have been created by Finsemble
	 *
	 * @param {*} nativeWindowObject
	 * @memberof AssimilationWindowManager
	 */
	addNativeWindow(nativeWindowObject: FinsembleNativeWindowObject): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.addNativeWindow:", nativeWindowObject);
		this.allWindows[nativeWindowObject.uuid] = nativeWindowObject;
		let self = this;

		const canHook = self.assimilationHelper.canHookWindow(nativeWindowObject);
		if (!canHook) return;

		Logger.system.debug(
			"Assimilation.AssimilationWindowManager.addNativeWindow: executableInConfig",
			nativeWindowObject
		);

		// We don't want to hook windows this way that are in our configs.
		this.assimilationHelper.executableInConfig(nativeWindowObject, (err: StandardError, inConfig: boolean) => {
			if (inConfig) return;
			nativeWindowObject.windowName = nativeWindowObject.name;
			RouterClient.query(
				"LauncherService.createWrap",
				{ name: nativeWindowObject.uuid },
				(createWrapError, response) => {
					if (createWrapError) {
						Logger.system.error("Error inside of createWrap:", createWrapError);
					}
					FinsembleWindow.getInstance(response.data, (error: StandardError, windowWrap: FinsembleWindow) => {
						if (error) {
							return Logger.system.error("Could not create wrap for window", nativeWindowObject);
						}
						self.activeWindows[nativeWindowObject.uuid] = windowWrap;
						windowWrap.uuid = nativeWindowObject.uuid;

						const { top, left, right, bottom } = nativeWindowObject;
						const nativeWindowObjectBounds: Bounds = {
							top,
							left,
							width: right - left,
							height: bottom - top,
						};
						self.handleSpawnComplete(windowWrap, nativeWindowObjectBounds);
					});
				}
			);
		});
	}

	/**
	 * moveWindow moves a window to the specified location
	 *
	 * @param {*} params
	 * @param {*} params.location
	 * @param {*} params.location.width
	 * @param {*} params.location.height
	 * @param {*} params.location.left
	 * @param {*} params.location.right
	 * @param {*} params.location.top
	 * @param {*} params.location.bottom
	 * @param {*} params.key - The uuid
	 * @param {*} ws
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	moveWindow(params: MoveWindowData) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.moveWindow:", params);
		console.log(params);
		if (!params.location) {
			return;
		}
		// if (!params.key) return;
		if (!params.name) return;
		let movingWin = this.getWindowByUUID(params.key);
		if (!movingWin) {
			movingWin = this.getWindowByName(params.name);
			if (!movingWin) {
				Logger.system.error("MoveWindow - could not find window. Name:", params.name, "Handle:", params.key);
				return;
			}
			// if we had to resort to finding a window by name, warn. This shouldn't be happening and should help ryan track down the root cause.
			Logger.system.warn(
				"MoveWindow - Could not find window by UUID, but did find window by name. Name:",
				params.name,
				"Handle:",
				params.key
			);
		}
		// Window calculations
		params.location.width = Math.round(params.location.width);
		params.location.bottom = Math.round(params.location.bottom);
		params.location.height = Math.round(params.location.height);
		params.location.left = Math.round(params.location.left);
		params.location.right = Math.round(params.location.right);
		if (isNaN(params.location.left)) return;
		if (isNaN(params.location.height)) {
			params.location.height = params.location.bottom - params.location.top;
			params.location.width = params.location.right - params.location.left;
		}

		movingWin.location = { ...params.location }; // Copy our data
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "moveWindow",
				uuid: movingWin.uuid,
				windowId: movingWin.uuid,
				location: params.location,
			})
		);
	}

	/**
	 *Show a window
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	showWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.showWindow:", message);
		if (message.data.name) {
			const win = this.getWindowByName(message.data.name);
			message.data.key = win.uuid;
		}
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "showWindow",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
	}

	/**
	 *hide a window
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	hideWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.hideWindow:", message);
		if (message.data.name) {
			const win = this.getWindowByName(message.data.name);
			message.data.key = win.uuid;
		}

		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "hideWindow",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
	}

	/**
	 * Dock Window to Monitor. This can be for any window, even those not managed by assimilation, so directly forwarding the message on.
	 * @param {*} message
	 */
	dockWindow(message: Record<string, any>): void {
		// If the dock call is during init, only allow it through one time. Docking calls after startup are always allowed.
		// This is a workaround for dockWindow being called more than once when the toolbar is being initialized. When these calls are too close together,
		// the OS ends up claiming double space. This is not ideal, but our system seems to allow for multiple calls to initialize a client causing us to end up
		// with two dockableWindow instances. We can't do any rate limiting in docking if the calls are coming from different instances so assimilation is the next possible spot.
		// This is also only preventing the claimed space portion of the dock; however, the extra setbounds call is not an issue since the values are the same each time.
		// Right now only the toolbar is dockable, if that ever changes this boolean would need to be tracked for each dockable window.
		if (!message.data.init || !this.dockedToolbarOnStart) {
			const formattedNativeHandle = `00000000${message.data.key}`.substr(-8);
			this.sendSocketMessage(
				this.assimilationHelper.prepareAction({
					topic: "dockWindow",
					uuid: formattedNativeHandle,
					windowId: formattedNativeHandle,
					edge: message.data.edge,
					bounds: message.data.bounds,
				})
			);
			this.dockedToolbarOnStart = true;
		}
	}

	/**
	 * Undock Window from Monitor. This can be for any window, even those not managed by assimilation, so directly forwarding the message on.
	 * @param {*} message
	 */
	undockWindow(message: Record<string, any>): void {
		const formattedNativeHandle = `00000000${message.data.key}`.substr(-8);
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "undockWindow",
				uuid: formattedNativeHandle,
				windowId: formattedNativeHandle,
			})
		);
	}

	/**
	 * Closes a window.
	 *
	 * This is the method to call when programmatically closing a
	 * native window (i.e, the Window Service calls this method).
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	closeWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.closeWindow:", message);
		const win = this.getWindowByName(message.data.name);

		// imesner: 20220404: Citrix windows were never getting a response, so send one here
		if (!win) return message?.sendQueryResponse(null, null);

		if (win.windowLess) {
			RouterClient.transmit(`Finsemble.Assimilation.close.${win.windowName}`, {});
			win.queryResponder = message.sendQueryResponse;
			/** DH 5/17/2019
			 * To our knowledge, this is only ever called when the workspace closes.
			 * Therefore, it's safe to pass in false for `removeFromWorkspace`
			 */
			this.removeWindow(win.uuid, false);
		} else if (win.spawnedByFea) {
			this._closeSpawned(win.finUUID, message.sendQueryResponse);
		} else {
			win.queryResponder = message.sendQueryResponse;
			this._closeAssimilationSpawned(win.uuid);
		}
	}

	/**
	 * Close a window that was spawned by Assimilation
	 *
	 * @param {*} uuid
	 * @memberof AssimilationWindowManager
	 */
	_closeAssimilationSpawned(uuid: string): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager._closeAssimilationSpawned:", uuid);
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "closeWindow",
				uuid,
				windowId: uuid,
			})
		);
		// sometimes assimilation needs multiple close requests
		setTimeout(() => {
			if (this.getWindowByUUID(uuid)) {
				this._closeAssimilationSpawned(uuid);
			}
		}, 1000);
	}

	/**
	 * Close a window
	 *
	 * @param {*} finUUID
	 * @memberof AssimilationWindowManager
	 */
	_closeSpawned(finUUID: string, queryResponder: Function): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager._closeSpawned:", finUUID);
		System.terminateExternalProcess(
			finUUID,
			0,
			(info) => {
				if (queryResponder) queryResponder(null, null);
				Logger.system.debug("Assimilation window closed:", info);
			},
			(reason) => {
				if (queryResponder) queryResponder(null, null);
				Logger.system.error("Assimilation window closed error:", reason);
			}
		);
	}

	/**
	 * Close all windows. This is only used on shutdown right now.
	 *
	 * @param {*} removeFromWorkspace
	 * @memberof AssimilationWindowManager
	 */
	closeAllWindows(removeFromWorkspace: string): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.closeAllWindows:", removeFromWorkspace);
		const windowKeys = Object.keys(this.activeWindows);
		windowKeys.forEach((key) => {
			RouterClient.transmit("LauncherService.shutdownResponse", {
				waitForMe: false,
				name: this.activeWindows[key].windowName,
			});
			this.removeWindow(key, false);
			this.sendSocketMessage(
				this.assimilationHelper.prepareAction({
					topic: "closeWindow",
					uuid: key,
					windowId: key,
				})
			);
		});
	}

	/**
	 * Bring a window to front
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	bringToFront(message: Record<string, any>): void {
		let win;
		Logger.system.debug("Assimilation.AssimilationWindowManager.bringToFront:", message);
		if (message.data.name) {
			win = this.getWindowByName(message.data.name);
			if (!win) {
				console.log("Failed to get window", message.data.name);
				return;
			}
			message.data.key = win.uuid;
		} else {
			win = this.getWindowByUUID(message.data.key);
		}
		if (win.state === "minimize") {
			this.showWindow(message);
		}

		// 'bringToFront' is just an action that toggles alwaysOnTop on then off again. If the window
		// is already alwaysOnTop, there is no need to do anything (we also don't want to toggle it off)
		if (win.descriptor?.alwaysOnTop) {
			return;
		}

		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "bringToFront",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
	}

	/**
	 *Minimize a window
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	minimizeWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.minimizeWindow:", message);
		const win = this.getWindowByName(message.data.name);
		if (message.data.name) {
			message.data.key = win.uuid;
		}
		win.state = "minimize";
		win.lastMinimized = Date.now();
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "minimize",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
	}

	/**
	 * Maximizes a window
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	maximizeWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.maximizeWindow:", message);
		if (message.data.name) {
			const win = this.getWindowByName(message.data.name);
			message.data.key = win.uuid;
			win.state = "maximize";
		}
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "maximize",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
	}

	/**
	 *Restore a window
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	restoreWindow(message: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.restoreWindow:", message);
		if (message.data.name) {
			const win = this.getWindowByName(message.data.name);
			message.data.key = win.uuid;
			// When we send a message off to assimilation to restore the window, we change the 'state'
			// of the window.Without this state change, we won't throw focus events.
			win.state = "restore";
		}
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "restore",
				uuid: message.data.key,
				windowId: message.data.key,
			})
		);
		// Changing this to use name because of key problems
		RouterClient.transmit(`${message.data.name}.restored`, {});
	}

	/**
	 *Set a window to always on top
	 *
	 * @param {*} message
	 * @memberof AssimilationWindowManager
	 */
	alwaysOnTop(message: Record<string, any>): void {
		let currentWindow;

		if (message.data.name && !message.data.key) {
			currentWindow = this.getWindowByName(message.data.name);
		} else {
			currentWindow = this.getWindowByUUID(message.data.key);
		}

		if (!currentWindow && !message.data.key) {
			// WPF calls this function twice since the header registers separate from the window
			// When the header calls, this will fail.
			// If the current window cant be found and a uuid is not supplied, return
			return;
		}

		Logger.system.debug("Assimilation.AssimilationWindowManager.alwaysOnTop:", message);
		this.sendSocketMessage(
			this.assimilationHelper.prepareAction({
				topic: "alwaysOnTop",
				uuid: message.data.key || currentWindow.uuid,
				alwaysOnTop: message.data.alwaysonTop,
			})
		);
		if (currentWindow.descriptor && currentWindow.descriptor.alwaysOnTop !== message.data.alwaysonTop) {
			currentWindow.updateOptions({ alwaysOnTop: message.data.alwaysonTop }, Function.prototype);
		}
	}

	/**
	 * Remove a window from our lists
	 *
	 * @param {*} uuid - The UUID of the window being removed
	 * @memberof AssimilationWindowManager
	 */
	removeWindow(uuid: string, removeFromWorkspace: boolean): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.removeWindow:", uuid);
		const currentWindow = this.getWindowByUUID(uuid);

		if (!currentWindow) {
			delete this.activeWindows[uuid];
			delete this.allWindows[uuid];
			return;
		}

		if (currentWindow.queryResponder) {
			// if the window was closed by Finsemble we add this queryResponder to the window.
			currentWindow.queryResponder(null, null);
		} else {
			// close window but set from assimilation so that we know that this window was closed outside Finsemble
			currentWindow.close({ fromAssimilation: true, removeFromWorkspace });
		}
		delete this.activeWindows[uuid];
		delete this.allWindows[uuid];
		RouterClient.query("Launcher.removeComponent", { name: currentWindow.windowName }, () => {});
	}

	/**
	 * parses an array of application passed back from assimilation. We currently only use this on startup
	 *
	 * @param {*} list
	 * @memberof AssimilationWindowManager
	 */
	parseApplicationList(list: string) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.parseApplicationList:", list);
		const parsedList = JSON.parse(list);

		// See if any windows have been removed.
		this.checkForRemoved(parsedList);
		for (let i = 0; i < parsedList.length; i++) {
			const item = parsedList[i];

			if (!this.allWindows[item.uuid]) {
				// If we don't already have the window add it
				Logger.system.debug(
					"Assimilation.AssimilationWindowManager.parseApplicationList invoking addNativeWindow:",
					item
				);

				this.addNativeWindow(item);
			}
		}
	}

	/**
	 * Check to see if windows have been removed since we last parsed this list.
	 *
	 * @param {*} list
	 * @memberof AssimilationWindowManager
	 */
	checkForRemoved(list: Record<string, any>[]) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.checkForRemoved:", list);
		let found: boolean = false;
		for (const key in this.allWindows) {
			found = false;
			// Exclude windowless components because they are not in the assimilation window list
			if (this.allWindows[key].windowLess) continue;
			for (let j = 0; j < list.length; j++) {
				if (list[j].uuid === this.allWindows[key].uuid) {
					found = true;
					break;
				}
			}

			if (!found) {
				if (this.activeWindows[key]) this.activeWindows[key].unRegisterWithDocking();
				delete this.activeWindows[key];
				delete this.allWindows[key];
			}
		}
	}

	/**
	 * This registers a window with the docker service and initializes the window with the launcher
	 *
	 * @param  {Object} windowObject Our window object(windowModel)
	 */
	registerWindow(windowMsg: Record<string, any>, windowObject: Record<string, any>): void {
		Logger.system.debug(
			"Assimilation.AssimilationWindowManager.registerWindow:",
			windowObject.windowName,
			windowMsg,
			windowObject
		);
		windowMsg.name = windowObject.windowName;
		const identifier = {
			name: windowObject.windowName,
			uuid: windowObject.uuid,
			windowType: "NativeWindow",
			// hack to get the window position data into docking. If no location, the component is windowless
			windowMsg: { location: windowMsg.location ? windowMsg.location : null },
			options: {
				minWidth: windowObject.descriptor?.minWidth || 0,
				minHeight: windowObject.descriptor?.minHeight || 0,
				maxWidth: windowObject.descriptor?.maxWidth || Infinity,
				maxHeight: windowObject.descriptor?.maxHeight || Infinity,
				snapping: windowObject.descriptor?.customData?.foreign?.services?.windowService?.allowSnapping,
				grouping: windowObject.descriptor?.customData?.foreign?.services?.windowService?.allowGrouping,
			},
		};

		// When the wrap is available in the launcher, register the window with docking.
		let wrapReadyChannel = `Finsemble.${windowObject.windowName}.wrapReady`;
		let wrapReadyCallback = (err: StandardError, response: SubscriberResponse<any>) => {
			windowObject.wrapState = "ready";
			windowObject.initializeWindow({
				identifier,
				manageWindowMovement: Boolean(windowMsg.location),
			});
			if (!(response.data && response.data.name === identifier.name)) return;
			if (response.data.state === "closed") return;
			RouterClient.transmit(`${windowMsg.name}.onSpawned`, identifier);

			const manifest = windowObject?.descriptor?.customData?.appDConfig?.manifest;
			// send boot status for every native apps except app service which should send it by itself
			if (manifest?.waitForInitialization !== true) {
				// let system manager's startup know the component's spawn is complete
				SystemManagerClient.publishBootStatus(windowObject.componentType, "components", "completed");
			}

			// disable this line becuase we need to write the code this way.
			RouterClient.unsubscribe(subscribeID); // eslint-disable-line
		};
		let subscribeID = RouterClient.subscribe(wrapReadyChannel, wrapReadyCallback);

		if (windowMsg.location) {
			// only hook components if they have a window.
			this.sendSocketMessage(
				this.assimilationHelper.prepareAction({
					topic: "hookWindow",
					uuid: windowObject.uuid,
				})
			);
		}
	}

	/**
	 * Unregister all windows with docking and remove them from the active window list
	 *
	 * @memberof AssimilationWindowManager
	 */
	unRegisterAllWindows(): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager.unRegisterAllWindows:");
		const windowSize = Object.keys(this.activeWindows).length;
		if (windowSize > 0) {
			for (const key in this.activeWindows) {
				this.activeWindows[key].unRegisterWithDocking();
			}
		}
		this.activeWindows = {};
	}

	/**
	 * Spawn an application with assimilation. This can be through assets or path
	 *
	 * @param {*} windowObject
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	spawnWindow(windowObject: Record<string, any>) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.spawnWindow:", windowObject);
		const globalUseFea = this.assimilationHelper.manifest.useFeaSpawn;
		const useFeaSpawn = windowObject.descriptor.customData.window.hasOwnProperty("useFeaSpawn")
			? windowObject.descriptor.customData.window.useFeaSpawn
			: globalUseFea;
		if (windowObject.descriptor.alias) {
			return useFeaSpawn ? this._spawnByAlias(windowObject) : this._SpawnByAlias(windowObject);
		}
		useFeaSpawn ? this._spawnByPathThroughContainer(windowObject) : this._spawnByPath(windowObject);
	}

	/**
	 *Save a window's state data
	 *
	 * @param {*} message
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	async saveWindow(message: Record<string, any>) {
		Logger.system.debug("Assimilation.AssimilationWindowManager.saveWindow:", message);
		const currentWindow = this.getWindowByUUID(message.data.key);
		if (!currentWindow) return;
		if (message.data.location) {
			currentWindow.location = message.data.location;
			if (!currentWindow.location.width)
				currentWindow.location.width = currentWindow.location.right - currentWindow.location.left;
			if (!currentWindow.location.height)
				currentWindow.location.height = currentWindow.location.bottom - currentWindow.location.top;
			currentWindow.descriptor.defaultLeft = currentWindow.location.left;
			currentWindow.descriptor.defaultTop = currentWindow.location.top;
			currentWindow.descriptor.defaultWidth = currentWindow.location.width;
			currentWindow.descriptor.defaultHeight = currentWindow.location.height;
			currentWindow.descriptor.defaultRight = currentWindow.location.left + currentWindow.location.width;
			currentWindow.descriptor.defaultBottom = currentWindow.location.top + currentWindow.location.height;
			currentWindow.descriptor.left = currentWindow.location.left;
			currentWindow.descriptor.top = currentWindow.location.top;
			currentWindow.descriptor.width = currentWindow.location.width;
			currentWindow.descriptor.height = currentWindow.location.height;
			currentWindow.descriptor.right = currentWindow.descriptor.defaultRight;
			currentWindow.descriptor.bottom = currentWindow.descriptor.defaultBottom;
		}

		currentWindow.descriptor.cacheLocation = currentWindow.restoreBounds;

		if (message.data.options && message.data.options.autoShow) {
			currentWindow.descriptor.autoShow = message.data.options.autoShow;
		}

		currentWindow.descriptor = Object.assign(currentWindow.descriptor, message.data);

		/** DH 5/21/2019
		 * Here we remove unneeded props so to reduce Router load and not unnecessarily
		 * dirty the workspace, but ideally there should be rigid definitions of what
		 * props go on each window, and this sort of thing should not be necessary.
		 */
		if (currentWindow.descriptor.customData) delete currentWindow.descriptor.customData.manifest;
		delete currentWindow.descriptor.url;

		if (!currentWindow.outsideOfFinsemble) {
			const windowData = await BaseWindow.cleanupState(currentWindow.descriptor);
			await setWindowState({
				windowName: currentWindow.descriptor.name,
				state: { windowData },
			});
		}
	}

	/**
	 *set the floating header component.
	 *
	 * @param {*} val
	 * @memberof AssimilationWindowManager
	 */
	setFloatingTitlebar(val: string | null): void {
		this.floatingTitlebar = val;
	}

	/**
	 *Get the floating header component
	 *
	 * @returns
	 * @memberof AssimilationWindowManager
	 */
	getFloatingTitlebar(): string | null {
		return this.floatingTitlebar;
	}

	/**
	 * Send heartbeat updates for all active assimilation windows
	 */
	updateActiveWindowHeartbeats(): void {
		setInterval(() => {
			for (const key in this.activeWindows) {
				const window = this.activeWindows[key];

				// Transmit heartbeat for assimilated (but not "native") components to prevent Finsemble from warning about them being crashed.
				// native (Finsemble.DLL and Finsemble.JAR) deal with sending heartbeats themselves.
				if (window.descriptor && !window.descriptor.isWPF) {
					RouterClient.transmit("Finsemble.heartbeat", {
						type: "component",
						windowName: window.windowName,
					});
				}
			}
		}, 1000);
	}

	/**
	 * Spawn a window by path
	 *
	 * @param {*} windowObject
	 * @memberof AssimilationWindowManager
	 */
	_spawnByPath(windowObject: Record<string, any>): void {
		console.log(`Spawning ${windowObject.name}`);
		Logger.system.debug("Assimilation.AssimilationWindowManager._spawnByPath:", windowObject);
		const ref = uuidv4();
		windowObject.ref = ref;

		this.awaitingAssimilationSpawn[ref] = windowObject;
		const json = {
			topic: "openNewApp",
			env: windowObject.descriptor.env,
			path: windowObject.descriptor.generatedPath || windowObject.descriptor.path, // This is the full path to the application
			arguments: windowObject.descriptor.generatedArguments || windowObject.descriptor.arguments, // This is command line arguments of the application
			autoShow: windowObject.descriptor.hasOwnProperty("autoShow") ? windowObject.descriptor.autoShow : true, // Should we show this window automatically?
			alwaysOnTop: windowObject.descriptor.hasOwnProperty("alwaysOnTop") ? windowObject.descriptor.alwaysOnTop : false,
			spawnDefaultViewer: windowObject.descriptor.hasOwnProperty("spawnDefaultViewer")
				? windowObject.descriptor.spawnDefaultViewer
				: false,
			ref, // A reference number so that this can come back and be referenced in other calls
			location: {
				left: windowObject.descriptor.defaultLeft,
				top: windowObject.descriptor.defaultTop,
				height: windowObject.descriptor.defaultHeight,
				width: windowObject.descriptor.defaultWidth,
			},
			windowTitleWhiteList: get(windowObject, "descriptor.customData.window.windowTitleWhiteList"),
			windowTitleBlackList: get(windowObject, "descriptor.customData.window.windowTitleBlackList"),
			windowSpawnTimeout: get(windowObject, "descriptor.customData.window.windowSpawnTimeout"),
		};
		this.sendSocketMessage(JSON.stringify(json));
	}

	/**
	 * Spawn the window by alias(app asset).
	 *
	 * @param {*} windowObject
	 * @memberof AssimilationWindowManager
	 */
	_SpawnByAlias(windowObject: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager._SpawnByAlias:", windowObject);
		const ref = uuidv4();
		windowObject.ref = ref;
		this.awaitingAssimilationSpawn[ref] = windowObject;
		const foundAsset = this.assimilationHelper.findAssetByAlias(
			windowObject.descriptor.alias || windowObject.descriptor.generatedPath || windowObject.descriptor.path
		); // Look for the asset.
		if (!foundAsset) return Logger.system.error("The alias or path for this asset is missing");

		const json = {
			topic: "openAssetApp",
			alias: foundAsset.alias,
			env: windowObject.descriptor.env,
			arguments: windowObject.descriptor.generatedArguments || windowObject.descriptor.arguments,
			version: foundAsset.version,
			target: foundAsset.target,
			autoShow: windowObject.descriptor.hasOwnProperty("autoShow") ? windowObject.descriptor.autoShow : true,
			spawnDefaultViewer: windowObject.descriptor.hasOwnProperty("spawnDefaultViewer")
				? windowObject.descriptor.spawnDefaultViewer
				: false,
			ref,
			location: {
				left: windowObject.descriptor.defaultLeft,
				top: windowObject.descriptor.defaultTop,
				height: windowObject.descriptor.defaultHeight,
				width: windowObject.descriptor.defaultWidth,
			},
			windowTitleWhiteList: get(windowObject, "descriptor.customData.window.windowTitleWhiteList"),
			windowTitleBlackList: get(windowObject, "descriptor.customData.window.windowTitleBlackList"),
			windowSpawnTimeout: get(windowObject, "descriptor.customData.window.windowSpawnTimeout"),
		};
		this.sendSocketMessage(JSON.stringify(json));
	}

	/**
	 * use to spawn our asset by path
	 *
	 * @param {*} windowObject
	 * @memberof AssimilationWindowManager
	 */
	_spawnByPathThroughContainer(windowObject: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager._spawnByPath:", windowObject);
		const ref = uuidv4();
		windowObject.ref = ref;
		windowObject.spawnedByFea = true;
		this.awaitingSpawn[ref] = windowObject;
		System.launchExternalProcess(
			{
				path: windowObject.descriptor.generatedPath || windowObject.descriptor.path,
				arguments: windowObject.descriptor.generatedArguments || windowObject.descriptor.arguments,
				listener: (event) => {
					// react to close event
					if (event.topic === "exited") {
						this.removeWindow(windowObject.uuid, true);
						Logger.system.log("Assimilation.AssimilationWindowManager:Exited External Process", event);
					} else {
						Logger.system.log("Assimilation.AssimilationWindowManager:Running External Process", event);
					}
				},
			},
			(payload) => {
				windowObject.finUUID = payload.uuid;
			},
			(err) => {
				Logger.system.error("Error in spawnByPath", err);
			}
		);
	}

	/**
	 * use to spawn our asset by alias
	 *
	 * @param {*} windowObject
	 * @memberof AssimilationWindowManager
	 */
	_spawnByAlias(windowObject: Record<string, any>): void {
		Logger.system.debug("Assimilation.AssimilationWindowManager._spawnByAlias:", windowObject);
		const ref = uuidv4();
		windowObject.ref = ref;
		windowObject.spawnedByFea = true;

		windowObject.appAsset = this.assimilationHelper.findAssetByAlias(windowObject.descriptor.alias);
		this.awaitingSpawn[ref] = windowObject;
		System.launchExternalProcess(
			{
				alias: windowObject.descriptor.alias,
				arguments: windowObject.descriptor.generatedArguments || windowObject.descriptor.arguments,
				listener: (event) => {
					// react to close event
					if (event.topic === "exited") {
						this.removeWindow(windowObject.uuid, true);
						Logger.system.log("Assimilation.AssimilationWindowManager:Exited External Process", event);
					} else {
						Logger.system.log("Assimilation.AssimilationWindowManager:Running External Process", event);
					}
				},
			},
			(payload) => {
				windowObject.finUUID = payload.uuid;
			},
			(err) => {
				Logger.system.error(err);
			}
		);
	}
}
